'use client';

import { Search } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useState, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { ScrollArea } from '@/components/ui/scroll-area';
import Link from 'next/link';
import type { SupportedLocale } from '@/lib/translation/types';
interface TreatmentMinimal {
  title: string;
  slug: string;
  brief_description?: string;
}
export default function TreatmentSearch({
  locale
}: {
  locale: SupportedLocale;
}) {
  const t = useTranslations('Navbar');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TreatmentMinimal[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [keepResultsVisible, setKeepResultsVisible] = useState(false);

  // Memoize the search function to prevent recreating on every render
  const searchTreatments = useMemo(() => debounce(async (searchQuery: string) => {
    // Don't search if query is too short
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    // Check sessionStorage cache first
    const cacheKey = `treatment-search-${locale}-${searchQuery}`;
    let cachedResults = null;
    try {
      const storedResults = sessionStorage.getItem(cacheKey);
      if (storedResults) {
        cachedResults = JSON.parse(storedResults);
      }
    } catch (e) {
      console.error('Failed to access sessionStorage for search cache:', e);
    }
    if (cachedResults) {
      setResults(cachedResults);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`/api/search-suggestions/treatments?locale=${locale}&q=${encodeURIComponent(searchQuery)}`);
      const data = await res.json();

      // Cache the results in sessionStorage
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(data));
      } catch (e) {
        console.error('Failed to store search results in sessionStorage:', e);
      }
      setResults(data);
    } catch (e) {
      console.error('Search failed:', e);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, 500),
  // Increased debounce to 500ms
  [locale]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      searchTreatments.cancel();
    };
  }, [searchTreatments]);
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    searchTreatments(value);
  }, [searchTreatments]);

  // Add keyboard navigation handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!results.length) {
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < results.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          window.location.href = `/treatment/${results[selectedIndex].slug}`;
        }
        break;
      case 'Escape':
        setQuery('');
        setResults([]);
        setSelectedIndex(-1);
        break;
      case 'Tab':
        // Keep results visible when tabbing
        setKeepResultsVisible(true);
        // Set first result as selected when tabbing out of search
        if (!e.shiftKey && results.length > 0) {
          setSelectedIndex(0);
        }
        break;
    }
  }, [results, selectedIndex]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [results]);

  // Handle key navigation in search results
  const handleResultKeyDown = useCallback((e: React.KeyboardEvent<HTMLAnchorElement | HTMLDivElement>, index: number) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < results.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        // If on first item and pressing up, move focus back to search input
        if (index === 0) {
          const searchInput = document.querySelector('.treatment-search-input') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
          }
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (index >= 0 && index < results.length) {
          window.location.href = `/treatment/${results[index].slug}`;
        }
        break;
    }
  }, [results]);
  const isSearching = query.length > 0 && isSearchFocused || query.length > 0 && keepResultsVisible && results.length > 0;

  // Add a click handler outside the search area to close results
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.treatment-search-container')) {
        setKeepResultsVisible(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Add a class for keeping the visual style of the search input when results are visible
  const searchInputClass = `absolute w-full h-full rounded-none bg-transparent md:focus:border-b md:focus:border-offblack ring-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-hidden text-base pl-8 pr-2 search-input treatment-search-input ${isSearching ? 'md:border-b md:border-offblack' : ''}`;
  return <div className="w-full flex-1 overflow-hidden md:px-0 px-4 treatment-search-container" data-sentry-component="TreatmentSearch" data-sentry-source-file="treatment-search.tsx">
      <div className="flex-none relative w-full h-12 mt-3 ">
        <Search className="absolute z-10 left-0 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" data-sentry-element="Search" data-sentry-source-file="treatment-search.tsx" />
        <input type="search" autoFocus placeholder={t('searchPlaceholder')} value={query} onChange={handleSearchChange} onKeyDown={handleKeyDown} onFocus={() => {
        setIsSearchFocused(true);
        setKeepResultsVisible(true);
      }} onBlur={() => {
        setTimeout(() => {
          setIsSearchFocused(false);
          // Don't immediately hide results to allow tabbing
          // They will be hidden if user clicks outside
        }, 200);
      }} className={searchInputClass} />
      </div>

      {isSearching && <ScrollArea className="h-full" onMouseDown={() => setKeepResultsVisible(true)}>
          {loading ? <div className="py-4 text-center text-gray-500">{t('searching')}</div> : results.length > 0 ? results.map((treatment, index) => <Link key={index} href={`/treatment/${treatment.slug}`} tabIndex={0} onKeyDown={e => handleResultKeyDown(e, index)} className={`block hover:bg-offbluish border-b border-offblack ${index === selectedIndex ? 'bg-offbluish' : ''}`}>
                <div className="py-4 px-4" onMouseEnter={() => setSelectedIndex(index)} onFocus={() => setSelectedIndex(index)}>
                  <h5 className="uppercase">{treatment.title}</h5>
                  {treatment.brief_description && <p className="text-sm w-full mt-2 text-gray-600">
                      {treatment.brief_description.length > 200 ? `${treatment.brief_description.slice(0, 200)}...` : treatment.brief_description}
                    </p>}
                </div>
              </Link>) : query.length >= 2 ? <div className="py-4 text-center text-gray-500">{t('noResults')}</div> : null}
        </ScrollArea>}
    </div>;
}